import { useId } from '@floating-ui/react';
import { Button } from '@hologram-dimension/button';
import { Popover } from '@hologram-dimension/popover';
import useSimStatuses from 'api/sims/useSimStatuses';
import useSimsCount from 'api/sims/useSimsCount';
import useSimsIds from 'api/sims/useSimsIds';
import { clsx } from 'clsx/lite';
import ExportSimsButton from 'components/ExportSimsButton/ExportSimsButton';
import ManageSimButton from 'components/ManageSimButton/ManageSimButton';
import useSimInventoryFilters from 'hooks/SimInventory/useSimInventoryFilters';
import { type FC, useCallback, useMemo } from 'react';
import useSimInventorySelected from 'state/SimInventory/useSimInventorySelected';
import useSimInventorySelectionStore from 'state/SimInventory/useSimInventorySelectionStore';
import useLegacyDashboard from 'state/contexts/LegacyDashboard/useLegacyDashboard';
import type { StructsSimRequestProperties } from 'types/__generated__/SimInventoryApi';
import type {
  ActivateButtonRenderer,
  OnSimsModified,
  OpenModalCallback,
  SimInventorySegmentName,
} from 'types/common';
import formatNumber from 'utils/numbers/formatNumber';
import styles from './SimInventoryActions.module.css';

interface SimInventoryActionsProps {
  className?: string;
  segmentName: SimInventorySegmentName;
  handleModalOpen: OpenModalCallback;
  onSimsModified: OnSimsModified;
  activateButtonRenderer?: ActivateButtonRenderer;
}

const SimInventoryActions: FC<SimInventoryActionsProps> = ({
  className,
  segmentName,
  handleModalOpen,
  onSimsModified,
  activateButtonRenderer,
}) => {
  const {
    filters,
    hasFilters,
    hasUserFilterApplied,
    filtersValidForAllSegment,
    error: filtersError,
    isLoading: isLoadingFilters,
  } = useSimInventoryFilters({
    segmentName,
  });

  const {
    networkStatusGroups,
    isLoading: simStatusesLoading,
    error: simStatusesError,
  } = useSimStatuses();

  const baseFilters: Partial<StructsSimRequestProperties> = {
    organizationId: filters?.organizationId,
  };
  if (segmentName !== 'all') {
    if (segmentName === 'active') {
      baseFilters.networkStatus = networkStatusGroups?.active;
    } else if (segmentName === 'preflight') {
      baseFilters.networkStatus = networkStatusGroups?.predeployment;
    } else {
      baseFilters.networkStatus = networkStatusGroups?.deactivated;
    }
  }

  const canLoadSimsCount =
    hasFilters &&
    (segmentName !== 'all' || filtersValidForAllSegment) &&
    !(filtersError || simStatusesError) &&
    !(isLoadingFilters || simStatusesLoading);

  // Below will return the "max" of the active, preflight and deactivated sections
  const {
    count: max,
    error: simsCountError,
    isLoading: countLoading,
  } = useSimsCount(baseFilters, canLoadSimsCount && segmentName !== 'all');

  const {
    count: filteredCount,
    error: filteredSimsError,
    isLoading: filteredSimsLoading,
  } = useSimsCount(filters, canLoadSimsCount);

  const {
    simIds: allFilteredSimIds,
    isLoading: allSimIdsLoading,
    error: allSimIdsError,
  } = useSimsIds({ filters, canLoad: canLoadSimsCount });

  const { resetSelection } = useSimInventorySelectionStore();

  const onDeselect = useCallback(() => {
    resetSelection(segmentName);
  }, [resetSelection, segmentName]);

  const { all, sims } = useSimInventorySelected(segmentName);

  const onRefreshClick = useCallback(() => {
    onSimsModified();
  }, [onSimsModified]);

  const error = simsCountError || filteredSimsError || allSimIdsError;
  const isLoading = countLoading || filteredSimsLoading || allSimIdsLoading;

  const popoverId = useId();

  const legacyDashboardContext = useLegacyDashboard();

  const hasSelection = all || (sims && sims.size > 0);
  const onPredeploymentActivateClick = useCallback(() => {
    if (!legacyDashboardContext) {
      throw new Error('Legacy dashboard context is not available');
    }

    if (!hasSelection) {
      return;
    }

    const { endTestingAndActivate } = legacyDashboardContext;

    const deviceIds: Array<DeviceId | undefined> = [...sims!.values()];
    endTestingAndActivate(
      deviceIds.filter<DeviceId>((id): id is DeviceId => !!id),
      'SIM Inventory'
    );
  }, [hasSelection, legacyDashboardContext, sims]);

  const content = useMemo(() => {
    if (error) {
      return <div className={styles.placeholderText}>We ran into an issue loading your SIMs</div>;
    }

    let title;
    let exportTitle;
    let buttonText;
    let countText;

    // TODO: Rewrite this to share text/logic and parameterize it all
    switch (segmentName) {
      case 'all':
        if (all === 'checked') {
          title = `${formatNumber(filteredCount)} filtered SIMs`;
          exportTitle = title;
          buttonText = 'Manage all SIMs';
        } else if (sims && sims.size > 0) {
          title = `${formatNumber(sims.size)} selected ${sims.size === 1 ? 'SIM' : 'SIMs'}`;
          exportTitle = title;
          buttonText = `Manage ${formatNumber(sims.size)} ${sims.size === 1 ? 'SIM' : 'SIMs'}`;
        } else {
          title = 'Select SIMs to take actions';
          exportTitle =
            filteredCount > 0 ? `${formatNumber(filteredCount)} filtered SIMs` : 'No SIMs found';
          buttonText = 'Manage SIMs';
        }

        if (hasUserFilterApplied) {
          countText =
            filteredCount > 0 ? `Showing ${formatNumber(filteredCount)} SIMs` : 'No SIMs found';
        }

        break;

      case 'active':
        // TODO: All the all states break on changing filters. They show -1 or something since none of the selected SIMs are in the filtered count
        if (all === 'checked') {
          title = `${formatNumber(filteredCount)} ${
            hasUserFilterApplied ? 'filtered' : 'active'
          } SIMs`;
          exportTitle = title;
          buttonText = 'Manage all SIMs';
        } else if (sims && sims.size > 0) {
          title = `${formatNumber(sims.size)} selected ${sims.size === 1 ? 'SIM' : 'SIMs'}`;
          exportTitle = title;
          buttonText = `Manage ${formatNumber(sims.size)} ${sims.size === 1 ? 'SIM' : 'SIMs'}`;
        } else {
          title = 'Select SIMs to take actions';
          exportTitle =
            filteredCount > 0
              ? `${formatNumber(filteredCount)} ${
                  hasUserFilterApplied ? 'filtered' : 'active'
                } SIMs`
              : 'No SIMs found';
          buttonText = 'Manage SIMs';
        }

        if (max > 0) {
          countText = hasUserFilterApplied
            ? `Showing ${formatNumber(filteredCount)} of ${formatNumber(max)} ${
                hasSelection ? '' : 'active '
              }SIMs`
            : `Showing all ${formatNumber(max)} ${hasSelection ? '' : 'active '}SIMs`;
        } else {
          countText = 'No active SIMs';
        }

        break;

      case 'preflight':
        if (all === 'checked') {
          title = `${formatNumber(filteredCount)} ${
            hasUserFilterApplied ? 'filtered' : 'pre-deployment'
          } SIMs`;
          exportTitle = title;
          buttonText = 'Manage all SIMs';
        } else if (sims && sims.size > 0) {
          title = `${formatNumber(sims.size)} selected ${sims.size === 1 ? 'SIM' : 'SIMs'}`;
          exportTitle = title;
          buttonText = `Manage ${formatNumber(sims.size)} ${sims.size === 1 ? 'SIM' : 'SIMs'}`;
        } else {
          title = 'Select SIMs to take actions';
          exportTitle = filteredCount
            ? `${formatNumber(filteredCount)} ${
                hasUserFilterApplied ? 'filtered' : 'pre-deployment'
              } SIMs`
            : 'No SIMs found';
          buttonText = 'Manage SIMs';
        }

        if (max > 0) {
          countText = hasUserFilterApplied
            ? `Showing ${formatNumber(filteredCount)} of ${formatNumber(max)} SIMs${
                hasSelection ? '' : ' in testing'
              }`
            : `Showing all ${formatNumber(max)} SIMs${hasSelection ? '' : ' in testing'}`;
        } else {
          countText = 'No SIMs in testing';
        }

        break;

      case 'deactivated':
        if (all === 'checked') {
          title = `${formatNumber(filteredCount)} ${
            hasUserFilterApplied ? 'filtered' : 'deactivated'
          } SIMs`;
          exportTitle = title;
          buttonText = 'Manage all SIMs';
        } else if (sims && sims.size > 0) {
          title = `${formatNumber(sims.size)} selected ${sims.size === 1 ? 'SIM' : 'SIMs'}`;
          exportTitle = title;
          buttonText = `Manage ${formatNumber(sims.size)} ${sims.size === 1 ? 'SIM' : 'SIMs'}`;
        } else {
          title = 'Select SIMs to take actions';
          exportTitle =
            filteredCount > 0
              ? `${formatNumber(filteredCount)} ${
                  hasUserFilterApplied ? 'filtered' : 'deactivated'
                } SIMs`
              : 'No SIMs found';
          buttonText = 'Manage SIMs';
        }

        if (max > 0) {
          countText = hasUserFilterApplied
            ? `Showing ${formatNumber(filteredCount)} of ${formatNumber(max)} ${
                hasSelection ? '' : 'deactivated '
              }SIMs`
            : `Showing all ${formatNumber(max)} ${hasSelection ? '' : 'deactivated '}SIMs`;
        } else {
          countText = 'No deactivated SIMs';
        }

        break;

      default:
    }

    const countToUse = sims?.size ?? 0;

    const simsToExport = !sims || sims.size === 0 ? allFilteredSimIds : sims;

    return (
      <>
        <div className={styles.leftContent}>
          <ManageSimButton
            subtitle={title}
            isLoading={isLoading}
            buttonText={buttonText}
            selectedSims={sims}
            disabled={error || !(all || (sims && sims.size > 0))}
            handleModalOpen={handleModalOpen}
            onSimsModified={onSimsModified}
            context="inventory"
            segmentName={segmentName}
          />
          {!(isLoading || error) &&
            (countToUse > 0 ? (
              <>
                <div className={styles.selectedText}>
                  {formatNumber(countToUse)} selected{`${countText ? ` (${countText})` : ''}`}
                </div>
                <Button
                  variant="tertiary"
                  onClick={onDeselect}
                  className={styles.deselectAll}
                  id="siminventory-selection-deselectall"
                >
                  Deselect all
                </Button>
              </>
            ) : (
              <div className={styles.selectedText}>{countText}</div>
            ))}
        </div>
        <div className={styles.rightContent}>
          <Popover id={popoverId!} content="Refresh the list of SIMs" allowPinning={false}>
            <Button
              iconStart="Sync"
              variant="secondary"
              disabled={isLoading}
              onClick={onRefreshClick}
              id="siminventory-actions-refresh"
            />
          </Popover>
          <ExportSimsButton
            subtitle={exportTitle}
            isLoading={isLoading}
            selectedSims={simsToExport}
            disabled={error || !(all || (simsToExport && simsToExport.size > 0))}
            segmentName={segmentName}
            id="siminventory-actions-export"
          />
          {segmentName === 'preflight' ? (
            <Button
              iconStart="Lightning"
              onClick={onPredeploymentActivateClick}
              disabled={!hasSelection}
            >
              {hasSelection ? `Activate ${sims!.size} SIMs` : 'Activate SIMs'}
            </Button>
          ) : (
            activateButtonRenderer?.({
              children: 'Activate SIMs',
              variant: 'primary',
              id: 'siminventory-actions-activate',
            })
          )}
        </div>
      </>
    );
  }, [
    activateButtonRenderer,
    all,
    allFilteredSimIds,
    error,
    filteredCount,
    handleModalOpen,
    hasSelection,
    hasUserFilterApplied,
    isLoading,
    max,
    onDeselect,
    onPredeploymentActivateClick,
    onRefreshClick,
    onSimsModified,
    popoverId,
    segmentName,
    sims,
  ]);

  return <div className={clsx(styles.wrapper, className)}>{content}</div>;
};

export default SimInventoryActions;
